import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { USER_TYPE } from '@plataforma/utils';
import ItemDetail from '../ItemDetail';
import DocumentStatus from '../DocumentStatus';

import * as S from './DocumentDetailWrapperStyles';

function DocumentDetail({
  children,
  name: policyHolder,
  issuedAt,
  process,
  federalId: policyHolderDocument,
  description: modality,
  insuredName,
  edital: processNumber,
  securedAmount,
  durationStart,
  durationEnd,
  duration,
  commissionValue,
  totalPremiumValue,
  customerControlIdentifies,
  observation,
  onClickActionButton,
  onClickPdfButton,
  quoteStatus = {},
  userType,
  showDocumentStatus,
  hasPermissionToReopenProcess,
  labelProcess,
  labelInsured,
  claimant,
  protocol,
  isDisabled,
  taxValue
}) {
  const { t } = useTranslation();

  const internalized = 'underAnalysis';
  return (
    <>
      <S.Title margin={!showDocumentStatus && !protocol}>
        {`${t(`policyDetail.${process.type}`)} ${process.processId}`}
      </S.Title>
      {issuedAt && !protocol && (
        <S.Date>{`${t('policyDetail.issuedAt')} ${issuedAt}`}</S.Date>
      )}
      {protocol && <S.Protocol>{protocol}</S.Protocol>}
      {quoteStatus?.groupName === internalized && (
        <S.ActionButton
          data-testid="btn-go-resume"
          variant="primary"
          isShortButton
          onClick={onClickPdfButton}
          disabled={isDisabled}
        >
          Baixar proposta
        </S.ActionButton>
      )}
      {showDocumentStatus && <DocumentStatus status={quoteStatus.groupName} />}
      <ItemDetail label={t('policyDetail.policyHolder')} value={policyHolder} />
      <ItemDetail
        label={t('policyDetail.policyHolderDocument')}
        value={policyHolderDocument}
      />
      <ItemDetail label={t('policyDetail.modalityLabel')} value={modality} />
      <ItemDetail label={t('policyDetail.claimantLabel')} value={claimant} />
      <ItemDetail
        label={t(`policyDetail.insured.${labelInsured}`)}
        value={insuredName}
        separator="top"
      />
      <ItemDetail
        label={t(`policyDetail.processNumber.${labelProcess}`)}
        value={processNumber}
        separator
      />
      <ItemDetail
        label={t('policyDetail.securedAmount')}
        value={securedAmount}
      />
      <S.Group>
        <ItemDetail
          label={t('policyDetail.durationStart')}
          value={durationStart}
        />
        <ItemDetail label={t('policyDetail.durationEnd')} value={durationEnd} />
        <ItemDetail label={t('policyDetail.duration')} value={duration} />
        {taxValue && (
          <ItemDetail label={t('policyDetail.taxValue')} value={taxValue} />
        )}
        {commissionValue && userType === USER_TYPE.broker && (
          <ItemDetail
            label={t('policyDetail.commissionValue')}
            value={commissionValue}
          />
        )}
        <ItemDetail
          label={t('policyDetail.totalPremiumValue')}
          value={totalPremiumValue}
        />
      </S.Group>
      <ItemDetail
        label={t('policyDetail.customerControlIdentifies')}
        value={customerControlIdentifies}
        separator
      />
      <ItemDetail label={t('policyDetail.observation')} value={observation} />
      <S.LawTextValue>{t('policyDetail.law382Message')}</S.LawTextValue>
      {children}
      {hasPermissionToReopenProcess && (
        <S.ActionButton
          data-testid="btn-go-resume"
          variant="primary"
          size="big"
          onClick={onClickActionButton}
        >
          {t('policyDetail.actionButton')}
        </S.ActionButton>
      )}
    </>
  );
}

DocumentDetail.propTypes = {
  children: PropTypes.element,
  process: PropTypes.shape({
    processId: PropTypes.string,
    type: PropTypes.string
  }),
  name: PropTypes.string,
  issuedAt: PropTypes.string,
  federalId: PropTypes.string,
  description: PropTypes.string,
  insuredName: PropTypes.string,
  edital: PropTypes.string,
  securedAmount: PropTypes.string,
  durationStart: PropTypes.string,
  durationEnd: PropTypes.string,
  duration: PropTypes.string,
  commissionValue: PropTypes.string,
  taxValue: PropTypes.string,
  totalPremiumValue: PropTypes.string,
  customerControlIdentifies: PropTypes.string,
  observation: PropTypes.string,
  onClickActionButton: PropTypes.func,
  onClickPdfButton: PropTypes.func,
  userType: PropTypes.string,
  showDocumentStatus: PropTypes.bool,
  isDisabled: PropTypes.bool,
  hasPermissionToReopenProcess: PropTypes.bool,
  labelProcess: PropTypes.string,
  labelInsured: PropTypes.string,
  claimant: PropTypes.string,
  quoteStatus: PropTypes.shape({
    groupName: PropTypes.string
  }),
  protocol: PropTypes.string
};

export default memo(DocumentDetail);
